import React, { Suspense, lazy, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { BrowserTracing, getClient, init as initSentry } from "@sentry/react";
import { isReportableError } from './services/apiError';
import {
  initializeThirdPartyTelemetry,
  scheduleAfterLoadAndIdle,
} from './services/thirdPartyTelemetry';
import './index.css';
import './App.css';
import { SimuladorProvider } from './components/Context_simulador/simulador_context';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18n from './i18n';

import Boas_vindas from './pages/boas_vindas';
import Termos from './pages/termos';
import ProtectedRoute from './components/ProtectedRoute'

const loadTecPec = () => import('./pages/tec_pec');
const Inicial = lazy(() => import('./pages/inicial'));
const Tec_pec = lazy(loadTecPec);
const Potencialize = lazy(() => import('./pages/potencialize'));
const Atingir_metas = lazy(() => import('./pages/atingir_metas'));
const Calibrar = lazy(() => import('./pages/calibrar'));
const Estoque_animal = lazy(() => import('./pages/estoque_animal'));
const Limite_investimento = lazy(() => import('./pages/limite_investimento'));
const Menu_principal = lazy(() => import('./pages/menu_principal'));
const Resultado_limite_investimento = lazy(() => import('./pages/resultado_limite_investimento'));
const Resultado_simulacao = lazy(() => import('./pages/resultado_simulacao'));
const Simulacao_base = lazy(() => import('./pages/simulacao_base'));
const Sensibilidade = lazy(() => import('./pages/sensibilidade'));
const Tabela_produtividade = lazy(() => import('./pages/gera_tabela_produtividade'));
const Resultado_metas = lazy(() => import('./pages/resultado_metas'));
const Perfil = lazy(() => import('./pages/perfil'));
const CompararSistemas = lazy(() => import('./pages/comparar_sistemas'));

const RouteLoader = () => {
  const { t } = useTranslation('common');
  return (
    <div className="motion-section flex min-h-[100dvh] w-full items-center justify-center bg-white px-6 text-center text-sm font-medium text-gray-600">
      {t('loading.screen')}
    </div>
  );
};

const withSuspense = (element) => (
  <Suspense fallback={<RouteLoader />}>
    {element}
  </Suspense>
);

const telemetrySetting = process.env.REACT_APP_ENABLE_TELEMETRY;
const isTelemetryEnabled = telemetrySetting
  ? telemetrySetting === 'true'
  : process.env.NODE_ENV === 'production';

// Configuração do Sentry
if (isTelemetryEnabled) {
  initSentry({
    dsn: process.env.REACT_APP_SENTRY_DSN || "https://8344e354c1b84aeab25402bb75c8a20e@bug.embrapa.io/95",
    integrations: [
      new BrowserTracing({
        tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
      })
    ],
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      if (hint?.originalException && !isReportableError(hint.originalException)) {
        return null;
      }
      return event;
    },
  });
}

let replayInitializationPromise = null;

const initializeSentryReplay = () => {
  if (!isTelemetryEnabled) return Promise.resolve(null);

  if (!replayInitializationPromise) {
    replayInitializationPromise = import('@sentry/replay')
      .then(({ replayIntegration }) => {
        const client = getClient();
        if (!client) return null;

        const replay = replayIntegration();
        client.addIntegration(replay);
        return replay;
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[telemetry] Não foi possível iniciar o Sentry Replay', error);
        }
        return null;
      });
  }

  return replayInitializationPromise;
};

function useDeferredTelemetry() {
  useEffect(() => scheduleAfterLoadAndIdle(() => {
    if (isTelemetryEnabled) {
      initializeThirdPartyTelemetry();
      initializeSentryReplay();
    }

    // Antecipar a próxima tela sem competir com a primeira renderização.
    window.setTimeout(() => {
      loadTecPec().catch(() => {});
    }, 1000);
  }), []);
}

// Função para inicializar o Matomo
function useMatomo() {
  useEffect(() => {
    let removeScript = () => {};
    let cancelSchedule = () => {};

    const initMatomo = () => {
      const _mtm = window._mtm || [];
      window._mtm = _mtm;
      _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });

      const script = document.createElement('script');
      script.async = true;
      script.src = "https://simulador-mybeef.alpha.agro.rocks/js/container_ABCD1234.js";
      document.head.appendChild(script);

      removeScript = () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    };

    const scheduleMatomo = () => {
      if ('requestIdleCallback' in window) {
        const idleId = window.requestIdleCallback(initMatomo, { timeout: 2000 });
        cancelSchedule = () => window.cancelIdleCallback(idleId);
        return;
      }

      const timeoutId = window.setTimeout(initMatomo, 1500);
      cancelSchedule = () => window.clearTimeout(timeoutId);
    };

    const handleLoad = () => {
      scheduleMatomo();
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad, { once: true });
    }

    return () => {
      window.removeEventListener('load', handleLoad);
      cancelSchedule();
      removeScript();
    };
  }, []);
}

// Componente principal que inicializa o Matomo e fornece o roteador
function MainApp() {
  useMatomo(); // Hook que ativa o Matomo (analytics)
  useDeferredTelemetry();

  return (
    <I18nextProvider i18n={i18n}>
    <SimuladorProvider>
      <React.StrictMode>
        <RouterProvider router={router} />
      </React.StrictMode>
    </SimuladorProvider>
    </I18nextProvider>
  );
}



// Configuração do Router
const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      // Rotas públicas (onboarding / cadastro / login)
      { path: "/", element: <Boas_vindas /> },
      { path: "/termos", element: <Termos /> },
      { path: "/potencialize", element: withSuspense(<Potencialize />) },
      { path: "/tec_pec", element: withSuspense(<Tec_pec />) },
      { path: "/tam_rebanho", element: <Navigate to="/tec_pec" replace /> },
      { path: "/inicial", element: withSuspense(<Inicial />) },
      // Rotas protegidas (exigem sessão ou simulador ativo)
      {
        element: <ProtectedRoute />,
        children: [
          { path: "/simulacao_base", element: withSuspense(<Simulacao_base />) },
          { path: "/menu_principal", element: withSuspense(<Menu_principal />) },
          { path: "/comparar-sistemas", element: withSuspense(<CompararSistemas />) },
          { path: "/calibrar", element: withSuspense(<Calibrar />) },
          { path: "/estoque_animal", element: withSuspense(<Estoque_animal />) },
          { path: "/atingir_metas", element: withSuspense(<Atingir_metas />) },
          { path: "/limite_investimento", element: withSuspense(<Limite_investimento />) },
          { path: "/resultado_limite_investimento", element: withSuspense(<Resultado_limite_investimento />) },
          { path: "/resultado_simulacao", element: withSuspense(<Resultado_simulacao />) },
          { path: "/sensibilidade", element: withSuspense(<Sensibilidade />) },
          { path: "/gera_tabela_produtividade", element: withSuspense(<Tabela_produtividade />) },
          { path: "/resultado_metas", element: withSuspense(<Resultado_metas />) },
          { path: "/perfil", element: withSuspense(<Perfil />) },
        ]
      },
    ]
  },
]);

// Renderização do React
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<MainApp />);

